import { Alert } from "../../../components/Alert/Alert";
import { Input } from "../../../components/Input/Input";
import { NumberInputWithUnit } from "../../../components/NumberInputWithUnit/NumberInputWithUnit";
import { FieldRow } from "../../projects/config/FieldRow";
import { SectionCard } from "../../projects/config/SectionCard";
import { DEFAULT_BOT_PROFILES } from "../config/defaults";
import type { BotProfile, BotTimeBand, BotsSimulationConfig } from "../config/types";
import { sharesTotal, type BotsErrors } from "./botsValidation";
import styles from "./BotsSettings.module.css";

interface Props {
  simulation: BotsSimulationConfig;
  profiles: BotProfile[];
  timeOfDay: BotTimeBand[];
  onSimulation: (patch: Partial<BotsSimulationConfig>) => void;
  onProfiles: (next: BotProfile[]) => void;
  onTimeOfDay: (next: BotTimeBand[]) => void;
  dirty: boolean;
  errors: BotsErrors;
  /** Whole-table advice the validator has no field to hang on. */
  warnings: string[];
}

/**
 * `bots.simulation`, `bots.profiles[]` and `bots.time_of_day[]` — how loud a
 * bot is, and when.
 *
 * The three belong in one card because they multiply: a profile's message
 * probability is scaled by the band the guild's local clock is in, and neither
 * runs at all outside the awake window. Splitting them would put a factor on
 * one screen and its multiplicand on another.
 *
 * Mockup: bots-page-shell.html §4, "Behaviour profiles & time of day".
 */
export function BehaviourSection({
  simulation,
  profiles,
  timeOfDay,
  onSimulation,
  onProfiles,
  onTimeOfDay,
  dirty,
  errors,
  warnings,
}: Props) {
  const total = sharesTotal(profiles);
  const maxFactor = Math.max(1, ...timeOfDay.map((b) => b.factor || 0));

  return (
    <SectionCard
      id="behaviour"
      title="Behaviour profiles & time of day"
      apiName="bots.profiles[] · bots.time_of_day[] · bots.simulation"
      dirty={dirty}
    >
      <FieldRow
        name="Awake window"
        apiName="bots.simulation.awake_window_minutes"
        hint="Background chatter only runs in guilds where a real player did something recently. Everywhere else bots stay silent and cost nothing. It quiets chat only: bots still come in to play on their own rhythm."
      >
        <NumberInputWithUnit
          unit="min"
          value={simulation.awake_window_minutes}
          onChange={(n) => onSimulation({ awake_window_minutes: n })}
          min={0}
          aria-label="Awake window"
        />
        <div className={styles.helper}>
          Counted from the last real action in the guild. Set to 0 to simulate
          around the clock, as the original design document describes.
        </div>
      </FieldRow>

      {/* STACKED, like every other row on this page whose control is a wide
          grid editor rather than a scalar. On the Config page this table had
          ~864px of control column and fitted; the Settings tab spends 258px on
          the section rail, which leaves a two-column row 606px against this
          grid's own 720px minimum — a horizontal scrollbar at every window
          size, because the page is pinned to --content-max and gets no wider.
          Stacked gives the grid the whole card, 842px. */}
      <FieldRow
        name="Activity profiles"
        apiName="bots.profiles[]"
        hint="Assigned once per bot for life. Share must add up to 100%."
        stacked
      >
        <ProfilesEditor
          rows={profiles}
          invalid={errors.profileRows}
          onChange={onProfiles}
        />
        {errors.profiles ? (
          <div className={styles.fieldError}>{errors.profiles}</div>
        ) : (
          <div className={styles.helper}>
            Shares total {total}% · every seeded guild is guaranteed at least
            one <code>leader</code>.
          </div>
        )}
        {warnings.map((w) => (
          <Alert key={w} variant="warn">
            {w}
          </Alert>
        ))}
      </FieldRow>

      <FieldRow
        name="Time of day"
        apiName="bots.time_of_day[]"
        hint="Decides when a bot comes into the game, and multiplies the message probability. Hours are local to the guild's country, not to the server."
        stacked
      >
        <TimeOfDayEditor
          rows={timeOfDay}
          errors={errors}
          maxFactor={maxFactor}
          onChange={onTimeOfDay}
        />
        {errors.timeOfDay ? (
          <div className={styles.fieldError}>{errors.timeOfDay}</div>
        ) : (
          <div className={styles.helper}>
            Bands cover the full 24 hours with no gaps or overlaps.
          </div>
        )}
      </FieldRow>
    </SectionCard>
  );
}

function ProfilesEditor({
  rows,
  invalid,
  onChange,
}: {
  rows: BotProfile[];
  invalid: number[];
  onChange: (next: BotProfile[]) => void;
}) {
  const patch = (i: number, p: Partial<BotProfile>) =>
    onChange(rows.map((r, j) => (i === j ? { ...r, ...p } : r)));

  return (
    <div className={styles.gridEditor}>
      <div className={styles.scroller}>
        <div className={`${styles.row} ${styles.profiles} ${styles.head}`}>
          <div>key</div>
          <div className={styles.num}>share</div>
          <div className={styles.num}>msg prob</div>
          <div className={styles.num}>interval, min</div>
          <div className={styles.num}>per day</div>
          <div className={styles.num}>cooldown, min</div>
          <div />
        </div>
        {rows.map((p, i) => (
          <div key={i} className={`${styles.row} ${styles.profiles}`}>
            <Input
              value={p.key}
              onChange={(e) => patch(i, { key: e.target.value })}
              aria-label={`Profile ${i + 1} key`}
              error={invalid.includes(i) ? " " : undefined}
            />
            <Input
              type="number"
              value={String(p.share)}
              onChange={(e) => patch(i, { share: Number(e.target.value) })}
              aria-label={`Profile ${i + 1} share`}
              error={invalid.includes(i) ? " " : undefined}
            />
            <Input
              type="number"
              value={String(p.message_probability)}
              onChange={(e) =>
                patch(i, { message_probability: Number(e.target.value) })
              }
              aria-label={`Profile ${i + 1} message probability`}
            />
            <div className={styles.pair}>
              <Input
                type="number"
                value={String(p.min_interval_minutes)}
                onChange={(e) =>
                  patch(i, { min_interval_minutes: Number(e.target.value) })
                }
                aria-label={`Profile ${i + 1} minimum interval`}
              />
              <span className={styles.dash}>—</span>
              <Input
                type="number"
                value={String(p.max_interval_minutes)}
                onChange={(e) =>
                  patch(i, { max_interval_minutes: Number(e.target.value) })
                }
                aria-label={`Profile ${i + 1} maximum interval`}
              />
            </div>
            <Input
              type="number"
              value={String(p.max_messages_per_day)}
              onChange={(e) =>
                patch(i, { max_messages_per_day: Number(e.target.value) })
              }
              aria-label={`Profile ${i + 1} messages per day`}
            />
            <div className={styles.pair}>
              <Input
                type="number"
                value={String(p.cooldown_min_minutes)}
                onChange={(e) =>
                  patch(i, { cooldown_min_minutes: Number(e.target.value) })
                }
                aria-label={`Profile ${i + 1} minimum cooldown`}
              />
              <span className={styles.dash}>—</span>
              <Input
                type="number"
                value={String(p.cooldown_max_minutes)}
                onChange={(e) =>
                  patch(i, { cooldown_max_minutes: Number(e.target.value) })
                }
                aria-label={`Profile ${i + 1} maximum cooldown`}
              />
            </div>
            <button
              type="button"
              className={styles.rowBtn}
              aria-label={`Remove profile ${p.key || i + 1}`}
              onClick={() => onChange(rows.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className={styles.addRow}
        // The first default is casual, so a new row arrives with casual's play
        // rhythm written into it — visible in Progression, and valid.
        onClick={() =>
          onChange([...rows, { ...DEFAULT_BOT_PROFILES()[0], key: "", share: 0 }])
        }
      >
        + Add profile
      </button>
    </div>
  );
}

function TimeOfDayEditor({
  rows,
  errors,
  maxFactor,
  onChange,
}: {
  rows: BotTimeBand[];
  errors: BotsErrors;
  maxFactor: number;
  onChange: (next: BotTimeBand[]) => void;
}) {
  const patch = (i: number, p: Partial<BotTimeBand>) =>
    onChange(rows.map((r, j) => (i === j ? { ...r, ...p } : r)));

  return (
    <div className={styles.gridEditor}>
      <div className={styles.scroller}>
        <div className={`${styles.row} ${styles.tod} ${styles.head}`}>
          <div>from</div>
          <div>to</div>
          <div className={styles.num}>factor</div>
          <div />
          <div />
        </div>
        {rows.map((b, i) => (
          <div key={i}>
            {errors.gaps
              .filter((g) => g.before === i)
              .map((g) => (
                <div key={`${g.from}-${g.to}`} className={styles.gapRow}>
                  <div>{g.from}</div>
                  <div>{g.to}</div>
                  <div>—</div>
                  <div>
                    {g.kind === "gap"
                      ? "not covered by any band"
                      : "covered by two bands"}
                  </div>
                  <div />
                </div>
              ))}
            <div className={`${styles.row} ${styles.tod}`}>
              <Input
                value={b.from}
                onChange={(e) => patch(i, { from: e.target.value })}
                aria-label={`Band ${i + 1} start`}
                error={errors.bandRows.includes(i) ? " " : undefined}
              />
              <Input
                value={b.to}
                onChange={(e) => patch(i, { to: e.target.value })}
                aria-label={`Band ${i + 1} end`}
              />
              <Input
                type="number"
                step="0.05"
                value={String(b.factor)}
                onChange={(e) => patch(i, { factor: Number(e.target.value) })}
                aria-label={`Band ${i + 1} factor`}
              />
              <div className={styles.barTrack}>
                <div
                  className={styles.bar}
                  style={{
                    width: `${Math.min(100, ((b.factor || 0) / maxFactor) * 100)}%`,
                  }}
                />
              </div>
              <button
                type="button"
                className={styles.rowBtn}
                aria-label={`Remove band ${i + 1}`}
                onClick={() => onChange(rows.filter((_, j) => j !== i))}
              >
                ✕
              </button>
            </div>
          </div>
        ))}
        {errors.gaps
          .filter((g) => g.before >= rows.length)
          .map((g) => (
            <div key={`${g.from}-${g.to}`} className={styles.gapRow}>
              <div>{g.from}</div>
              <div>{g.to}</div>
              <div>—</div>
              <div>not covered by any band</div>
              <div />
            </div>
          ))}
      </div>
      <button
        type="button"
        className={styles.addRow}
        onClick={() =>
          onChange([...rows, { from: "00:00", to: "00:00", factor: 1 }])
        }
      >
        + Add band
      </button>
    </div>
  );
}
